// @ts-nocheck
import { Divider, Grid, Typography } from '@mui/material';
import { BasicButton } from '@tassat/button';
import { getStateOptions } from '@tassat/common-definitions';
import { SelectField, SimpleErrorMessage, TextField } from '@tassat/formik-input';
import { WithdrawalTypeEnum } from '@tassat/fred-definitions';
import { ActionTypes } from '@tassat/ui-definitions';
import * as React from 'react';

import {
    IFormProps,
    bankAccountTypes,
    blockchainTypes,
    withdrawalInstructionTypes
} from './types';

export const FormContents = (props: IFormProps) => {
    const {
        classes,
        values,
        errors,
        formProps,
        submitForm,
        isSubmitting,
        submitError,
        isValid,
        dirty,
        setFieldValue,
        onClose
    } = props;

    const {
        editWithdrawalInstruction,
        actionType,
        deleteWithdrawalInstruction
    } = formProps;

    const stateOptions = getStateOptions('isoCode');

    // INFO: The below hooks will convert US State Dropdown to text if the field changes to text
    // and will reset values for Bene Bank ABA/BIC depending on the country code
    React.useEffect(() => {
        if (values.WithdrawalType.value !== WithdrawalTypeEnum.US) {
            if (typeof values.BeneBankStateProv === 'object') {
                setFieldValue('BeneBankStateProv', values.BeneBankStateProv.value);
            }

            if (values.BeneBankRoutNum) {
                setFieldValue('BeneBankRoutNum', '');
            }
        }

        if (values.WithdrawalType.value === WithdrawalTypeEnum.US) {
            setFieldValue('BeneBankCountry', 'US');

            if (values.BeneBankBic) {
                setFieldValue('BeneBankBic', '');
            }
        }
    }, [
        values.WithdrawalType.value,
        values.BeneBankStateProv,
        values.BeneBankBic,
        values.BeneBankRoutNum,
        setFieldValue
    ]);

    React.useEffect(() => {
        if (values.BeneCountry !== 'US' && typeof values.BeneStateProv === 'object') {
            setFieldValue('BeneStateProv', values.BeneStateProv.value);
        }
    }, [
        values.BeneCountry,
        values.BeneStateProv,
        setFieldValue
    ]);

    React.useEffect(() => {
        if (values.IntermediaryBankCountry !== 'US' && typeof values.IntermediaryBankStateProv === 'object') {
            setFieldValue('IntermediaryBankStateProv', values.IntermediaryBankStateProv.value);
        }
    }, [
        values.IntermediaryBankCountry,
        values.IntermediaryBankStateProv,
        setFieldValue
    ]);

    // INFO: US/IN forms show the beneficiary/bank/intermediary/remittance sections; Blockchain shows
    // Blockchain/Address instead. Values are not cleared on switch so users don't lose data if they
    // toggle back (see FRED-6697 - behavior pending product confirmation).
    const isBankBasedWithdrawal = values.WithdrawalType.value === WithdrawalTypeEnum.US ||
        values.WithdrawalType.value === WithdrawalTypeEnum.IN;
    const isBlockchainWithdrawal = values.WithdrawalType.value === WithdrawalTypeEnum.BLOCKCHAIN;

    return (
        <form className={classes.flexColumn}>
            <Grid
                container
                direction='column'
                spacing={0}
            >
                <Grid
                    container
                    direction='row'
                    item
                    justifyContent='space-around'
                    sm={12}
                >
                    <Grid
                        item
                        md={5}
                        sm={12}
                    >
                        <TextField
                            errors={errors}
                            label='Withdrawal Description'
                            maxLength={48}
                            name='Description'
                            placeholder='Enter Withdrawal Description'
                            values={values}
                        />
                    </Grid>
                    <Grid
                        item
                        md={5}
                        sm={12}
                    >
                        <SelectField
                            autoComplete
                            disabled={actionType === ActionTypes.edit}
                            errors={errors}
                            label='Withdrawal Type'
                            menuPosition={values.WithdrawalType.value ? 'absolute' : 'fixed'}
                            name='WithdrawalType'
                            options={withdrawalInstructionTypes}
                            values={values}
                        />
                    </Grid>
                </Grid>
                {values?.WithdrawalType?.value &&
                    <>
                        {isBankBasedWithdrawal && (
                        <>
                            <Typography
                                className={classes.sectionHeader}
                                variant='h2'
                            >
                                Beneficiary
                            </Typography>
                            <Divider
                                className={classes.sectionDivider}
                                orientation='horizontal'
                                variant='fullWidth'
                            />
                            <Grid
                                container
                                direction='row'
                                item
                                justifyContent='space-around'
                                sm={12}
                            >
                                <Grid
                                    item
                                    md={5}
                                    sm={12}
                                >
                                    <TextField
                                        errors={errors}
                                        label='Name'
                                        maxLength={45}
                                        name='BeneName'
                                        placeholder='Enter Name'
                                        values={values}
                                    />
                                </Grid>
                                <Grid
                                    item
                                    md={5}
                                    sm={12}
                                >
                                    <TextField
                                        errors={errors}
                                        label={values.WithdrawalType.value === WithdrawalTypeEnum.US ?
                                        'Account Number'
                                        : 'International Account Number'}
                                        maxLength={34}
                                        name='BeneBankAcct'
                                        placeholder={values.WithdrawalType.value === WithdrawalTypeEnum.US ?
                                        'Enter Account Number'
                                        : 'Enter International Account Number'}
                                        values={values}
                                    />
                                </Grid>
                            </Grid>
                            <Grid
                                container
                                direction='row'
                                item
                                justifyContent='space-around'
                                sm={12}
                            >
                                <Grid
                                    item
                                    md={5}
                                    sm={12}
                                >
                                    <TextField
                                        errors={errors}
                                        label='Country Code'
                                        maxLength={2}
                                        name='BeneCountry'
                                        placeholder='Enter 2 Character ISO Code'
                                        values={values}
                                    />
                                </Grid>
                                <Grid
                                    item
                                    md={5}
                                    sm={12}
                                >
                                    <TextField
                                        errors={errors}
                                        label='Street Address'
                                        maxLength={35}
                                        name='BeneStreetAddress'
                                        placeholder='Enter Street Address'
                                        values={values}
                                    />
                                </Grid>
                            </Grid>
                            <Grid
                                container
                                direction='row'
                                item
                                justifyContent='space-around'
                                sm={12}
                            >
                                <Grid
                                    item
                                    md={5}
                                    sm={12}
                                >
                                    <TextField
                                        errors={errors}
                                        label='City'
                                        maxLength={21}
                                        name='BeneCity'
                                        placeholder='Enter City'
                                        values={values}
                                    />
                                </Grid>
                                <Grid
                                    item
                                    md={5}
                                    sm={12}
                                >
                                    {!errors.BeneCountry && values.BeneCountry === 'US' ? (
                                        <SelectField
                                            autoComplete
                                            errors={errors}
                                            label='State'
                                            name='BeneStateProv'
                                            options={stateOptions}
                                            values={values}
                                        />
                                ) : (
                                    <TextField
                                        errors={errors}
                                        label={(!values.BeneCountry || errors.BeneCountry) ?
                                            'State/Province'
                                            : 'Province (Optional)'}
                                        maxLength={21}
                                        name='BeneStateProv'
                                        placeholder={(!values.BeneCountry || errors.BeneCountry) ?
                                            'Enter State/Province'
                                            : 'Enter Province (Optional)'}
                                        values={values}
                                    />
                                )}
                                </Grid>
                            </Grid>
                            <Grid
                                container
                                direction='row'
                                item
                                justifyContent='space-around'
                                sm={12}
                            >
                                <Grid
                                    item
                                    md={5}
                                    sm={12}
                                >
                                    <TextField
                                        errors={errors}
                                        label='Postal Code/Zip (Optional)'
                                        maxLength={10}
                                        name='BenePostalCode'
                                        placeholder='Enter Postal Code/Zip'
                                        values={values}
                                    />
                                </Grid>
                                <Grid
                                    item
                                    md={5}
                                    sm={12}
                                >
                                    {/* Placeholder for spacing for above postal code */}
                                </Grid>
                            </Grid>
                            <Typography
                                className={classes.sectionHeader}
                                variant='h2'
                            >
                                Beneficiary Bank
                            </Typography>
                            <Divider
                                className={classes.sectionDivider}
                                orientation='horizontal'
                                variant='fullWidth'
                            />
                            <Grid
                                container
                                direction='row'
                                item
                                justifyContent='space-around'
                                sm={12}
                            >
                                <Grid
                                    item
                                    md={5}
                                    sm={12}
                                >
                                    <TextField
                                        errors={errors}
                                        label='Name'
                                        maxLength={45}
                                        name='BeneBankName'
                                        placeholder='Enter Name'
                                        values={values}
                                    />
                                </Grid>
                                <Grid
                                    item
                                    md={5}
                                    sm={12}
                                >
                                    <SelectField
                                        autoComplete
                                        errors={errors}
                                        label='Account Type'
                                        name='BeneBankAccountType'
                                        options={bankAccountTypes}
                                        values={values}
                                    />
                                </Grid>
                            </Grid>
                            <Grid
                                container
                                direction='row'
                                item
                                justifyContent='space-around'
                                sm={12}
                            >
                                <Grid
                                    item
                                    md={5}
                                    sm={12}
                                >
                                    {values.WithdrawalType.value === WithdrawalTypeEnum.US ? (
                                        <TextField
                                            errors={errors}
                                            label='ABA Number'
                                            maxLength={9}
                                            name='BeneBankRoutNum'
                                            placeholder='Enter ABA Number'
                                            values={values}
                                        />
                                ) : (
                                    <TextField
                                        errors={errors}
                                        label='SWIFT BIC'
                                        maxLength={11}
                                        name='BeneBankBic'
                                        placeholder='Enter SWIFT BIC'
                                        values={values}
                                    />
                                )}
                                </Grid>
                                <Grid
                                    item
                                    md={5}
                                    sm={12}
                                >
                                    <TextField
                                        disabled={values.WithdrawalType.value === WithdrawalTypeEnum.US}
                                        errors={errors}
                                        label='Country Code'
                                        maxLength={2}
                                        name='BeneBankCountry'
                                        placeholder='Enter 2 Character ISO Code'
                                        values={values}
                                    />
                                </Grid>
                            </Grid>
                            <Grid
                                container
                                direction='row'
                                item
                                justifyContent='space-around'
                                sm={12}
                            >
                                <Grid
                                    item
                                    md={5}
                                    sm={12}
                                >
                                    <TextField
                                        errors={errors}
                                        label='Street Address'
                                        maxLength={35}
                                        name='BeneBankStreetAddress'
                                        placeholder='Enter Street Address'
                                        values={values}
                                    />
                                </Grid>
                                <Grid
                                    item
                                    md={5}
                                    sm={12}
                                >
                                    <TextField
                                        errors={errors}
                                        label='City'
                                        maxLength={21}
                                        name='BeneBankCity'
                                        placeholder='Enter City'
                                        values={values}
                                    />
                                </Grid>
                            </Grid>
                            <Grid
                                container
                                direction='row'
                                item
                                justifyContent='space-around'
                                sm={12}
                            >
                                <Grid
                                    item
                                    md={5}
                                    sm={12}
                                >
                                    {values.WithdrawalType.value === WithdrawalTypeEnum.US ? (
                                        <SelectField
                                            autoComplete
                                            errors={errors}
                                            label='State'
                                            name='BeneBankStateProv'
                                            options={stateOptions}
                                            values={values}
                                        />
                                ) : (
                                    <TextField
                                        errors={errors}
                                        label='Province (Optional)'
                                        maxLength={21}
                                        name='BeneBankStateProv'
                                        placeholder='Enter Province'
                                        values={values}
                                    />
                                )}
                                </Grid>
                                <Grid
                                    item
                                    md={5}
                                    sm={12}
                                >
                                    <TextField
                                        errors={errors}
                                        label='Postal Code/Zip (Optional)'
                                        maxLength={10}
                                        name='BeneBankPostalCode'
                                        placeholder='Enter Postal Code/Zip'
                                        values={values}
                                    />
                                </Grid>
                            </Grid>
                            <Typography
                                className={classes.sectionHeader}
                                variant='h2'
                            >
                                Intermediary Bank (Optional)
                            </Typography>
                            <Divider
                                className={classes.sectionDivider}
                                orientation='horizontal'
                                variant='fullWidth'
                            />
                            <Grid
                                container
                                direction='row'
                                item
                                justifyContent='space-around'
                                sm={12}
                            >
                                <Grid
                                    item
                                    md={5}
                                    sm={12}
                                >
                                    <TextField
                                        errors={errors}
                                        label='Name'
                                        maxLength={45}
                                        name='IntermediaryBankName'
                                        placeholder='Enter Name'
                                        values={values}
                                    />
                                </Grid>
                                <Grid
                                    item
                                    md={5}
                                    sm={12}
                                >
                                    <TextField
                                        errors={errors}
                                        label='ABA Number'
                                        maxLength={9}
                                        name='IntermediaryBankRoutNum'
                                        placeholder='Enter ABA Number'
                                        values={values}
                                    />
                                </Grid>
                            </Grid>
                            <Grid
                                container
                                direction='row'
                                item
                                justifyContent='space-around'
                                sm={12}
                            >
                                <Grid
                                    item
                                    md={5}
                                    sm={12}
                                >
                                    <TextField
                                        errors={errors}
                                        label='SWIFT BIC'
                                        maxLength={35}
                                        name='IntermediaryBankBic'
                                        placeholder='Enter SWIFT BIC'
                                        values={values}
                                    />
                                </Grid>
                                <Grid
                                    item
                                    md={5}
                                    sm={12}
                                >
                                    <TextField
                                        errors={errors}
                                        label='Country Code'
                                        maxLength={2}
                                        name='IntermediaryBankCountry'
                                        placeholder='Enter 2 Character ISO Code'
                                        values={values}
                                    />
                                </Grid>
                            </Grid>
                            <Grid
                                container
                                direction='row'
                                item
                                justifyContent='space-around'
                                sm={12}
                            >
                                <Grid
                                    item
                                    md={5}
                                    sm={12}
                                >
                                    <TextField
                                        errors={errors}
                                        label='Street Address'
                                        maxLength={35}
                                        name='IntermediaryBankStreetAddress'
                                        placeholder='Enter Street Address'
                                        values={values}
                                    />
                                </Grid>
                                <Grid
                                    item
                                    md={5}
                                    sm={12}
                                >
                                    <TextField
                                        errors={errors}
                                        label='City'
                                        maxLength={21}
                                        name='IntermediaryBankCity'
                                        placeholder='Enter City'
                                        values={values}
                                    />
                                </Grid>
                            </Grid>
                            <Grid
                                container
                                direction='row'
                                item
                                justifyContent='space-around'
                                sm={12}
                            >
                                <Grid
                                    item
                                    md={5}
                                    sm={12}
                                >
                                    {!errors.IntermediaryBankCountry && values.IntermediaryBankCountry === 'US' ? (
                                        <SelectField
                                            autoComplete
                                            errors={errors}
                                            label='State'
                                            name='IntermediaryBankStateProv'
                                            options={stateOptions}
                                            values={values}
                                        />
                                ) : (
                                    <TextField
                                        errors={errors}
                                        label={(!values.IntermediaryBankCountry || errors.IntermediaryBankCountry) ?
                                            'State/Province'
                                            : 'Province (Optional)'}
                                        maxLength={21}
                                        name='IntermediaryBankStateProv'
                                        placeholder={(!values.IntermediaryBankCountry || errors.IntermediaryBankCountry) ?
                                            'Enter State/Province'
                                            : 'Enter Province (Optional)'}
                                        values={values}
                                    />
                                )}
                                </Grid>
                                <Grid
                                    item
                                    md={5}
                                    sm={12}
                                >
                                    <TextField
                                        errors={errors}
                                        label='Postal Code/Zip (Optional)'
                                        maxLength={10}
                                        name='IntermediaryBankPostalCode'
                                        placeholder='Enter Postal Code/Zip'
                                        values={values}
                                    />
                                </Grid>
                            </Grid>
                            <Typography
                                className={classes.sectionHeader}
                                variant='h2'
                            >
                                Remittance Information (Optional)
                            </Typography>
                            <Divider
                                className={classes.sectionDivider}
                                orientation='horizontal'
                                variant='fullWidth'
                            />
                            <Grid
                                container
                                direction='row'
                                item
                                justifyContent='space-around'
                                sm={12}
                            >
                                <Grid
                                    item
                                    md={5}
                                    sm={12}
                                >
                                    <TextField
                                        errors={errors}
                                        label='Beneficiary Information'
                                        maxLength={35}
                                        name='BeneInfo1'
                                        placeholder='Enter Beneficiary Information 1'
                                        values={values}
                                    />
                                </Grid>
                                <Grid
                                    item
                                    md={5}
                                    sm={12}
                                >
                                    <TextField
                                        errors={errors}
                                        label='Bank to Bank Information'
                                        maxLength={35}
                                        name='BankToBankInfo1'
                                        placeholder='Enter Bank to Bank Information 1'
                                        values={values}
                                    />
                                </Grid>
                            </Grid>
                            <Grid
                                container
                                direction='row'
                                item
                                justifyContent='space-around'
                                sm={12}
                            >
                                <Grid
                                    item
                                    md={5}
                                    sm={12}
                                >
                                    <TextField
                                        errors={errors}
                                        maxLength={35}
                                        name='BeneInfo2'
                                        placeholder='Enter Beneficiary Information 2'
                                        values={values}
                                    />
                                </Grid>
                                <Grid
                                    item
                                    md={5}
                                    sm={12}
                                >
                                    <TextField
                                        errors={errors}
                                        maxLength={35}
                                        name='BankToBankInfo2'
                                        placeholder='Enter Bank to Bank Information 2'
                                        values={values}
                                    />
                                </Grid>
                            </Grid>
                            <Grid
                                container
                                direction='row'
                                item
                                justifyContent='space-around'
                                sm={12}
                            >
                                <Grid
                                    item
                                    md={5}
                                    sm={12}
                                >
                                    <TextField
                                        errors={errors}
                                        maxLength={35}
                                        name='BeneInfo3'
                                        placeholder='Enter Beneficiary Information 3'
                                        values={values}
                                    />
                                </Grid>
                                <Grid
                                    item
                                    md={5}
                                    sm={12}
                                >
                                    <TextField
                                        errors={errors}
                                        maxLength={35}
                                        name='BankToBankInfo3'
                                        placeholder='Enter Bank to Bank Information 3'
                                        values={values}
                                    />
                                </Grid>
                            </Grid>
                            <Grid
                                container
                                direction='row'
                                item
                                justifyContent='space-around'
                                sm={12}
                            >
                                <Grid
                                    item
                                    md={5}
                                    sm={12}
                                >
                                    <TextField
                                        errors={errors}
                                        maxLength={35}
                                        name='BeneInfo4'
                                        placeholder='Enter Beneficiary Information 4'
                                        values={values}
                                    />
                                </Grid>
                                <Grid
                                    item
                                    md={5}
                                    sm={12}
                                >
                                    <TextField
                                        errors={errors}
                                        maxLength={35}
                                        name='BankToBankInfo4'
                                        placeholder='Enter Bank to Bank Information 4'
                                        values={values}
                                    />
                                </Grid>
                            </Grid>
                        </>
                    )}
                        {isBlockchainWithdrawal && (
                        <>
                            <Typography
                                className={classes.sectionHeader}
                                variant='h2'
                            >
                                Blockchain Information
                            </Typography>
                            <Divider
                                className={classes.sectionDivider}
                                orientation='horizontal'
                                variant='fullWidth'
                            />
                            <Grid
                                container
                                direction='row'
                                item
                                justifyContent='space-around'
                                sm={12}
                            >
                                <Grid
                                    item
                                    md={11}
                                    sm={12}
                                >
                                    <SelectField
                                        autoComplete
                                        errors={errors}
                                        label='Blockchain'
                                        name='Blockchain'
                                        options={blockchainTypes}
                                        values={values}
                                    />
                                </Grid>
                            </Grid>
                            <Grid
                                container
                                direction='row'
                                item
                                justifyContent='space-around'
                                sm={12}
                            >
                                <Grid
                                    item
                                    md={11}
                                    sm={12}
                                >
                                    <TextField
                                        errors={errors}
                                        label='Address'
                                        maxLength={64}
                                        name='Address'
                                        placeholder='Enter Address'
                                        values={values}
                                    />
                                </Grid>
                            </Grid>
                        </>
                    )}
                        <Grid
                            alignItems='center'
                            className={classes.submitButtonContainer}
                            container
                            item
                            justifyContent='space-evenly'
                            xs={12}
                        >
                            {actionType === ActionTypes.edit && (
                            <Grid item>
                                <BasicButton
                                    onClick={() => deleteWithdrawalInstruction &&
                                        deleteWithdrawalInstruction(editWithdrawalInstruction)}
                                    text='Delete instruction'
                                    type='action'
                                />
                            </Grid>
                        )}
                            <Grid
                                alignItems='center'
                                className={classes.submitButton}
                                container
                                direction='column'
                                item
                            >
                                <BasicButton
                                    disabled={!dirty ||
                                        isSubmitting ||
                                        !isValid}
                                    onClick={submitForm}
                                    text={actionType === ActionTypes.edit ? 'Submit' : 'Create new instruction'}
                                    type='submit'
                                />
                                <SimpleErrorMessage
                                    className={classes.submitError}
                                    error={submitError}
                                />
                            </Grid>
                        </Grid>
                    </>}
            </Grid>
        </form>
    );
};